/**
 * BanterSystem — contextual character quips triggered by gameplay events.
 *
 * Listens to EventBus for puzzle completions, ability activations, NPC calming,
 * and idle time, then surfaces short in-character lines via the HUD toast system.
 *
 * Design constraints:
 *   - At most 1 quip per COOLDOWN_MS to avoid spam
 *   - No consecutive repeats within a category
 *   - Idle banter triggers after IDLE_THRESHOLD_MS of no input
 *   - Fully decoupled — communicates only via EventBus
 */

import { Scene } from "@babylonjs/core/scene";
import { KeyboardEventTypes } from "@babylonjs/core/Events/keyboardEvents";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { BANTER, BanterCategory, BanterQuip } from "../config/dialogue";

// ─── Tuning ────────────────────────────────────────────────────

/** Minimum milliseconds between any two quips. */
const COOLDOWN_MS = 8_000;

/** Milliseconds of inactivity before idle banter fires. */
const IDLE_THRESHOLD_MS = 15_000;

/** Interval between idle-banter checks (ms). */
const IDLE_CHECK_INTERVAL_MS = 3_000;

/** Speaker label colors matching HUD character indicators. */
const SPEAKER_COLORS: Record<string, string> = {
  Minna: "#00ff88",
  Basil: "#ff8844",
};

// ─── System ────────────────────────────────────────────────────

export class BanterSystem {
  private scene: Scene;

  /** Unsubscribe functions returned by EventBus.on(). */
  private unsubs: (() => void)[] = [];

  /** Track the last quip index shown per category to avoid repeats. */
  private lastIndex: Map<BanterCategory, number> = new Map();

  /** Timestamp of the last quip shown (any category). */
  private lastQuipTime = 0;

  /** Timestamp of the last player input detected. */
  private lastInputTime: number = Date.now();

  /** Hard gate while cutscenes are active so HUD banter cannot overlap story beats. */
  private cutsceneActive = false;

  /** Handle for the idle-check interval. */
  private idleInterval: ReturnType<typeof setInterval> | null = null;

  /** Babylon keyboard observer handle. */
  private keyboardObserver: ReturnType<
    Scene["onKeyboardObservable"]["add"]
  > | null = null;

  constructor(scene: Scene) {
    this.scene = scene;
    this.subscribe();
    this.startIdleTracker();
  }

  // ─── Event subscriptions ───────────────────────────────────

  private subscribe(): void {
    // Puzzle solved
    this.unsubs.push(
      gameEvents.on(GameEvents.PUZZLE_SOLVED, () => {
        this.showQuip("puzzleSolved");
      })
    );

    // Ability activated — route per-ability so generic Basil quips
    // like "Harness locked" aren't chosen when he uses Leap. Gemini
    // 3.1-pro fluidity review caught this as a false-positive
    // "Anchor triggered instead of Leap" bug — in truth the Leap
    // fired correctly, but the banter line was an anchor-themed
    // quip from the generic pool. Per-ability pools make the
    // telegraphing match the action.
    this.unsubs.push(
      gameEvents.on(GameEvents.ABILITY_ACTIVATED, (data: unknown) => {
        this.markActivity();
        const payload = data as { abilityId?: string; character?: string } | undefined;
        const abilityId = payload?.abilityId ?? "";
        const minnaAbilityKeyMap: Record<string, string> = {
          auraSight: "abilityMinnaAuraSight",
          shadowSlip: "abilityMinnaShadowSlip",
          calmingResonance: "abilityMinnaPurr",
        };
        const basilAbilityKeyMap: Record<string, string> = {
          pneumaticAnchor: "abilityBasilAnchor",
          overclockedLeap: "abilityBasilLeap",
          turnTableTouch: "abilityBasilTurnTable",
        };
        if (minnaAbilityKeyMap[abilityId]) {
          this.showQuip(minnaAbilityKeyMap[abilityId], /*fallback*/ "abilityMinna");
        } else if (basilAbilityKeyMap[abilityId]) {
          this.showQuip(basilAbilityKeyMap[abilityId], /*fallback*/ "abilityBasil");
        } else if (
          payload?.character === "Minna"
          || abilityId.toLowerCase().includes("aura")
          || abilityId.toLowerCase().includes("shadow")
          || abilityId.toLowerCase().includes("calming")
        ) {
          this.showQuip("abilityMinna");
        } else {
          this.showQuip("abilityBasil");
        }
      })
    );

    // Co-op abilities
    this.unsubs.push(
      gameEvents.on(GameEvents.RESONANT_DROP_TRIGGERED, () => {
        this.showQuip("abilityCoop");
      })
    );
    this.unsubs.push(
      gameEvents.on(GameEvents.CHRONOS_BUBBLE_TRIGGERED, () => {
        this.showQuip("abilityCoop");
      })
    );

    // NPC calmed
    this.unsubs.push(
      gameEvents.on(GameEvents.NPC_CALMED, () => {
        this.showQuip("npcCalmed");
      })
    );

    // Portal opened
    this.unsubs.push(
      gameEvents.on(GameEvents.PORTAL_OPENED, () => {
        this.showQuip("portalOpened");
      })
    );

    // Crystal charged
    this.unsubs.push(
      gameEvents.on(GameEvents.CRYSTAL_CHARGED, () => {
        this.showQuip("crystalCharged");
      })
    );

    // Activity markers (reset idle timer on movement-related events)
    const activityEvents = [
      GameEvents.PLAYER_JUMPED,
      GameEvents.PLAYER_SWITCHED,
      GameEvents.PLAYER_LANDED,
    ];
    for (const evt of activityEvents) {
      this.unsubs.push(
        gameEvents.on(evt, () => this.markActivity())
      );
    }
    
    // Separation tether clamped banter quips when cats drift too far
    this.unsubs.push(
      gameEvents.on(GameEvents.PLAYER_SEPARATION_CLAMPED, () => {
        this.showQuip("cameraTetherClamped");
      })
    );

    // Suppress banter during cutscenes
    this.unsubs.push(
      gameEvents.on(GameEvents.CUTSCENE_STARTED, () => {
        this.cutsceneActive = true;
        this.lastQuipTime = Date.now() + 5_000; // push cooldown forward
      })
    );
    this.unsubs.push(
      gameEvents.on(GameEvents.CUTSCENE_ENDED, () => {
        this.cutsceneActive = false;
        this.lastInputTime = Date.now();
      })
    );
  }

  // ─── Idle tracking ────────────────────────────────────────

  private startIdleTracker(): void {
    // Listen for raw keyboard input as activity signal
    this.keyboardObserver = this.scene.onKeyboardObservable.add((info) => {
      if (info.type === KeyboardEventTypes.KEYDOWN) {
        this.markActivity();
      }
    });

    this.idleInterval = setInterval(() => {
      const elapsed = Date.now() - this.lastInputTime;
      if (elapsed >= IDLE_THRESHOLD_MS) {
        this.showQuip("idle");
        // Reset so the next idle quip waits another full threshold
        this.lastInputTime = Date.now();
      }
    }, IDLE_CHECK_INTERVAL_MS);
  }

  private markActivity(): void {
    this.lastInputTime = Date.now();
  }

  // ─── Quip selection & display ─────────────────────────────

  private showQuip(category: string, fallback?: BanterCategory): void {
    if (this.cutsceneActive) {
      return;
    }
    const now = Date.now();
    if (now - this.lastQuipTime < COOLDOWN_MS) {
      return; // still in cooldown
    }

    // Try the primary pool; if it's missing or empty fall back to the
    // caller's fallback category (used by per-ability Basil pools that
    // may be sparse on purpose).
    const primaryPool = (BANTER as Record<string, BanterQuip[]>)[category];
    const pool = primaryPool && primaryPool.length > 0
      ? primaryPool
      : (fallback ? BANTER[fallback] : undefined);
    const effectiveCategory = (primaryPool && primaryPool.length > 0 ? category : fallback) as BanterCategory | undefined;
    if (!pool || pool.length === 0 || !effectiveCategory) {
      return;
    }

    const quip = this.pickRandom(effectiveCategory, pool);
    this.lastQuipTime = now;

    const color = SPEAKER_COLORS[quip.speaker] ?? "#d4c5a9";
    const message = `${quip.speaker}: ${quip.text}`;

    gameEvents.emit(GameEvents.HUD_TOAST, { message, color });
  }

  /**
   * Pick a random quip from the pool, avoiding the last-used index
   * for that category.
   */
  private pickRandom(category: BanterCategory, pool: BanterQuip[]): BanterQuip {
    const lastIdx = this.lastIndex.get(category) ?? -1;

    if (pool.length === 1) {
      this.lastIndex.set(category, 0);
      return pool[0];
    }

    let idx: number;
    do {
      idx = Math.floor(Math.random() * pool.length);
    } while (idx === lastIdx);

    this.lastIndex.set(category, idx);
    return pool[idx];
  }

  // ─── Cleanup ──────────────────────────────────────────────

  dispose(): void {
    for (const unsub of this.unsubs) {
      unsub();
    }
    this.unsubs.length = 0;

    if (this.idleInterval !== null) {
      clearInterval(this.idleInterval);
      this.idleInterval = null;
    }

    if (this.keyboardObserver !== null) {
      this.scene.onKeyboardObservable.remove(this.keyboardObserver);
      this.keyboardObserver = null;
    }
  }
}
